import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== PUBLIC API ====================
export const publicApi = {
  // Tours
  getTours: (params = {}) => api.get('/tours', { params }),
  getFeaturedTours: () => api.get('/tours/featured'),
  getTourBySlug: (slug) => api.get(`/tours/${slug}`),
  getToursByCategory: (category) => api.get(`/tours/category/${category}`),
  
  // Enquiries
  createEnquiry: (data) => api.post('/enquiries', data),
  
  // Blogs
  getBlogs: (params = {}) => api.get('/blogs', { params }),
  getBlogBySlug: (slug) => api.get(`/blogs/${slug}`),
  
  // Testimonials
  getTestimonials: () => api.get('/testimonials'),
  createTestimonial: (data) => api.post('/testimonials', data),
  
  // Settings
  getSettings: () => api.get('/settings'),
  
  // Gallery
  getGallery: (params = {}) => api.get('/gallery', { params }),
  getGalleryCategories: () => api.get('/gallery/categories'),
  getGalleryByCategory: (category) => api.get(`/gallery/category/${category}`),
  
  // Contact
  submitContact: (data) => api.post('/contact', data),
};

// ==================== AUTH API ====================
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post(`/auth/forgot-password?email=${encodeURIComponent(email)}`),
  resetPassword: (token, newPassword) => api.post(`/auth/reset-password/${token}?new_password=${encodeURIComponent(newPassword)}`),
  changePassword: (currentPassword, newPassword) => api.post(`/auth/change-password?current_password=${encodeURIComponent(currentPassword)}&new_password=${encodeURIComponent(newPassword)}`),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  verifyToken: () => api.post('/auth/verify-token'),
};

// ==================== ADMIN API ====================
export const adminApi = {
  // Auth (legacy - redirecting to authApi)
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  
  // Dashboard
  getStats: () => api.get('/admin/dashboard/stats'),
  
  // Tours
  getTours: (params = {}) => api.get('/admin/tours', { params }),
  getTour: (id) => api.get(`/admin/tours/${id}`),
  createTour: (data) => api.post('/admin/tours', data),
  updateTour: (id, data) => api.put(`/admin/tours/${id}`, data),
  deleteTour: (id) => api.delete(`/admin/tours/${id}`),
  
  // Enquiries
  getEnquiries: (params = {}) => api.get('/admin/enquiries/', { params }),
  getEnquiry: (id) => api.get(`/admin/enquiries/${id}`),
  updateEnquiryStatus: (id, data) => api.patch(`/admin/enquiries/${id}/status`, data),
  updateEnquiry: (id, data) => api.put(`/admin/enquiries/${id}`, data),
  deleteEnquiry: (id) => api.delete(`/admin/enquiries/${id}`),
  exportEnquiries: (params = {}) => api.get('/admin/enquiries/export', { params, responseType: 'blob' }),
  getEnquiryStats: () => api.get('/admin/enquiries/stats'),
  bulkUpdateStatus: (ids, status) => api.post(`/admin/enquiries/bulk-update-status?status=${status}`, ids),
  bulkDelete: (ids) => api.post('/admin/enquiries/bulk-delete', ids),
  
  // Blogs
  getBlogs: (params = {}) => api.get('/admin/blogs', { params }),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (id, data) => api.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id) => api.delete(`/admin/blogs/${id}`),
  
  // Testimonials
  getTestimonials: (params = {}) => api.get('/admin/testimonials', { params }),
  approveTestimonial: (id) => api.put(`/admin/testimonials/${id}/approve`),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Gallery
  getGallery: (params = {}) => api.get('/admin/gallery/', { params }),
  uploadGalleryImage: (formData) => api.post('/admin/gallery/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateGalleryImage: (id, data) => api.put(`/admin/gallery/${id}`, data),
  deleteGalleryImage: (id) => api.delete(`/admin/gallery/${id}`),
  
  // Logout
  logout: () => api.post('/auth/logout'),
  
  // Seed Data
  seedData: () => api.post('/admin/seed'),
};

export default api;
